<template>
    <MuvalModal
        v-model="open"
        :closeOnOutside="false"
        :rounded="true"
        title="Complete your payment via bank transfer"
        size="payment"
    >
        <div class="font-body">
            <div class="text-muval-gray-1 text-sm">Your payment is needed to confirm your booking request.</div>
            <ol>
                <li class="relative flex flex-row gap-2 py-4 text-sm">
                    <!-- Connector Line -->
                    <div class="absolute h-full w-5">
                        <div class="relative flex h-full w-full">
                            <div class="absolute h-full w-full bg-white pt-7 pb-2 pl-0">
                                <div class="relative flex h-full w-full">
                                    <div
                                        class="border-grey-3 absolute left-1/2 h-full w-px border border-s border-dashed"
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Timeline Icon -->
                    <div class="relative flex h-6 w-6 items-center">
                        <span class="h-6 w-6 rounded-full bg-[#F7F7F7] text-center text-xs leading-6 font-medium"
                            >1</span
                        >
                    </div>
                    <div>
                        Log into your internet banking account, and make a payment using the reference number below:
                        <table class="mt-2">
                            <tbody>
                                <tr>
                                    <td class="text-muval-gray-2 p-1 px-2 pl-0 text-xs font-medium">Bank</td>
                                    <td class="p-1 px-2 text-xs font-medium">
                                        {{ app.tenantBankAccount.bank_name }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-muval-gray-2 p-1 px-2 pl-0 text-xs font-medium">BSB</td>
                                    <td class="p-1 px-2 text-xs font-medium">
                                        {{ app.tenantBankAccount.bsb }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-muval-gray-2 p-1 px-2 pl-0 text-xs font-medium">Account number</td>
                                    <td class="text-xxs p-1 px-2 font-medium">
                                        {{ app.tenantBankAccount.account_number }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-muval-gray-2 p-1 px-2 pl-0 text-xs font-medium">Account name</td>
                                    <td class="p-1 px-2 text-xs font-medium">
                                        {{ app.tenantBankAccount.account_name }}
                                    </td>
                                </tr>
                                <tr>
                                    <td class="text-muval-gray-2 p-1 px-2 pl-0 text-xs font-medium">Amount</td>
                                    <td class="p-1 px-2 text-xs font-medium">{{ paymentAmount }}</td>
                                </tr>
                                <tr>
                                    <td class="text-muval-gray-2 p-1 px-2 pl-0 text-xs font-medium">
                                        Message/reference
                                    </td>
                                    <td class="p-1 px-2 text-xs font-medium">{{ session.getOrderRef }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </li>
                <li class="relative flex flex-row gap-2 py-2 text-sm">
                    <div class="relative flex h-6 w-6 items-center">
                        <span class="h-6 w-6 rounded-full bg-[#F7F7F7] text-center text-xs leading-6 font-medium"
                            >2</span
                        >
                    </div>
                    <div>
                        Please email proof of your bank transfer (e.g. a payment receipt or screenshot) to
                        <a
                            :href="`mailto:${app.tenant?.email}?subject=Bank%20transfer%20for%20order%20%23${session.getOrderRef}`"
                            target="_blank"
                            class="link"
                            >{{ app.tenant?.email }}</a
                        >.
                    </div>
                </li>
            </ol>
            <div class="py-1 pt-4 text-sm font-medium md:text-xs">Important information</div>
            <p class="text-muval-gray-2 text-xs">
                Please note that we are unable to confirm your order and reserve the space for your booking until funds
                have been received in our account. This is usually the next business day when you transfer before 2pm
                AEST. However, times may vary between banks, especially when your transfer is made outside of business
                hours, or close to a weekend or public holiday. Please
                <a
                    class="link text-muval-gray-2"
                    @click="open = false"
                    >pay via card</a
                >
                if you require more immediate booking confirmation.
            </p>
        </div>

        <div class="items-top mt-5 flex gap-2 border-t-2 pt-5">
            <input
                id="terms-bank"
                v-model="termsBox"
                type="checkbox"
                class="rounded-muval-1 border-muval-gray-3 mt-1 cursor-pointer text-black focus:ring-0 focus:outline-hidden"
            />
            <label
                for="terms-bank"
                class="text-muval-gray-2 text-xs"
            >
                By proceeding you confirm that you have read and agree to the
                <router-link
                    :to="{ name: 'TermsAndConditions' }"
                    class="text-muval-gray-1 underline"
                >
                    terms and conditions</router-link
                >
                and
                <router-link
                    :to="{ name: 'PrivacyPolicy' }"
                    class="text-muval-gray-1 underline"
                >
                    privacy policy
                </router-link>
                .
            </label>
        </div>

        <MuvalBanner
            v-if="message"
            type="warning"
            icon="system-alert"
            inline
            class="mt-5"
        >
            {{ message }}
        </MuvalBanner>

        <div class="flex flex-row items-center justify-between pt-6">
            <div class="flex flex-col">
                <span class="text-sm leading-4 font-medium">
                    <span class="text-muval-gray-1">{{ paymentAmount }}</span>
                    <span class="pl-1 text-[#9B9B9B]">{{ paymentLabel }}</span>
                </span>
            </div>
            <MuvalButton
                type="submit"
                alternate
                :loading="reserveBankLoading"
                :disabled="!termsBox || reserveBankLoading"
                @click.prevent="handleReserveBankDeposit"
            >
                I have completed my bank transfer
            </MuvalButton>
        </div>
    </MuvalModal>
</template>

<script setup>
import MuvalModal from '@/components/MuvalModal.vue';
import { useSessionStore, usePaymentStore, useAppStore } from '@/store';

const app = useAppStore();
const session = useSessionStore();
const payment = usePaymentStore();

const props = defineProps({
    orderPaymentId: {
        type: [String, Number, null],
        default: null,
    },
    orderItemId: {
        type: [String, Number, null],
        required: false,
        default: null,
    },
    // Several services reserved together. Wins over `orderItemId`.
    orderItemIds: {
        type: Array,
        required: false,
        default: () => [],
    },
    paymentAmount: {
        type: String,
        default: null,
        required: true,
    },
    paymentLabel: {
        type: String,
        required: false,
        default: '',
    },
});

const termsBox = ref(false);
const reserveBankLoading = ref(false);
const message = ref(null);

const emit = defineEmits(['payment:success']);

const open = computed({
    get: () => app.getBankDepositModalOpen,
    set: (val) => {
        if (val) {
            app.openBankDepositModal();
        } else {
            app.closeBankDepositModal();
        }
    },
});

async function handleReserveBankDeposit() {
    if (reserveBankLoading.value) {
        return;
    }

    reserveBankLoading.value = true;
    message.value = null;

    try {
        if (props.orderPaymentId) {
            await payment.flagBankDeposit(props.orderPaymentId);
        } else {
            const ids =
                props.orderItemIds?.length ? props.orderItemIds
                : props.orderItemId != null ? [props.orderItemId]
                : [];
            await session.pendingBankDeposit(ids.length ? ids.map((id) => ({ id })) : null);

            emit('payment:success');
        }

        // Only on success: a rejected confirmation must leave the customer in the bank-transfer
        // step with the reason, not on a booking-complete screen.
        app.closeBankDepositModal();
        app.closePaymentModal();
    } catch (error) {
        console.log('error bank deposit', error);

        message.value = error?.message ?? 'An unexpected error occurred.';
    } finally {
        reserveBankLoading.value = false;
    }

    session.retrieve();
}
</script>
